import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { IndustryType, MinifiedDispensary, MUOBankResponse, OrganizationType } from '@gcv/shared';
import { DueDiligenceStatus, NonCannabisIndustryTypes } from '@gcv/shared';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box } from '@mui/system';

import { ReactComponent as HelpIcon } from 'assets/images/ic-help.svg';
import { ReactComponent as MuoIcon } from 'assets/images/muo-location-icon.svg';
import { ReactComponent as NonCannabisIcon } from 'assets/images/store-location-generic-icon.svg';
import { ReactComponent as DispensaryIcon } from 'assets/images/store-location-icon.svg';
import { useComponent } from 'hooks/useComponent';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import type { Column, Row } from 'ui';
import {
  Button,
  Container,
  ContainerItem,
  Spinner,
  Table,
  TableContainer,
  Text,
  Tooltip,
  ZeroState
} from 'ui';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { getLicenseType } from 'util/org.util';
import { capitalizeWords } from 'util/strings.util';
import { ChangeStatusModal } from '../components/change-status-modal';
import { model } from '../model';

interface ActiveCrbRow {
  id: string;
  dispensary_name: string;
  parent_muo_name?: string;
  city: string;
  state: string;
  license_type: string;
  reviewed_on: string;
  compliance_score: number | undefined;
  orgType: OrganizationType;
  hideCannabisRelatedContent?: boolean;
  nonPlantTouching?: boolean;
  internalId?: string[];
  relationship_manager: string;
  due_diligence: string;
  industryType?: IndustryType;
}

export const Active: React.FC = useComponent(() => {
  const navigate = useNavigate();
  const bankStore = getFiBankStore();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const dispStore = getFiDispensaryStore();
  const appViewStore = getAppViewStateStore();

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState<boolean>(false);
  const columns: Column<ActiveCrbRow>[] = [
    {
      label: 'Name',
      id: 'dispensary_name',
      widthPercent: 20,
      format: (row) => (
        <span style={{ display: 'flex' }}>
          {row.orgType === 'muo' ? (
            <MuoIcon style={{ marginRight: '9px', width: '20px', height: '20px' }}></MuoIcon>
          ) : row.industryType && NonCannabisIndustryTypes.includes(row.industryType) ? (
            <NonCannabisIcon style={{ marginRight: '9px', width: '20px', height: '20px' }} />
          ) : (
            <DispensaryIcon style={{ marginRight: '9px', width: '20px', height: '20px' }}></DispensaryIcon>
          )}
          {row.parent_muo_name ? (
            <Box flex="1">
              <Container padding="0" column justify="flex-start">
                <Text type="body3" content={row.dispensary_name} />
                <Text
                  type="body3"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                  content={row.parent_muo_name}
                />
              </Container>
            </Box>
          ) : (
            <Box flex="1">
              <Text type="body3" content={row.dispensary_name} />
            </Box>
          )}
        </span>
      ),
      sortFunction: (a, b) => {
        return a.data.dispensary_name.toLowerCase() > b.data.dispensary_name.toLowerCase() ? 1 : -1;
      }
    },
    {
      label: 'Relationship Manager',
      id: 'relationship_manager',
      widthPercent: 15
    },
    {
      label: 'City',
      id: 'city',
      widthPercent: 10,
      format: (row) => capitalizeWords(row.city)
    },
    {
      label: 'State',
      id: 'state',
      widthPercent: 10
    },
    {
      label: 'License Type',
      id: 'license_type',
      widthPercent: 15,
      format: (row) => capitalizeWords(row.license_type)
    },
    {
      label: 'Due Diligence',
      id: 'due_diligence',
      widthPercent: 15,
      format: (row) => capitalizeWords(row.due_diligence)
    },
    {
      label: 'Reviewed On',
      id: 'reviewed_on',
      widthPercent: 15,
      format: (row) => {
        return row.reviewed_on
          ? DateTimeHelpers.formatISOToTableDateString(row.reviewed_on, bankStore.bank.iana_timezone)
          : '--';
      }
    },
    {
      label: 'Compliance Score',
      id: 'compliance_score',
      widthPercent: 15,
      format: (row) => {
        if (row.hideCannabisRelatedContent) {
          return '--';
        } else if (row.nonPlantTouching) {
          return (
            <span style={{ display: 'flex' }}>
              <span style={{ paddingRight: '4px' }}>No Score</span>
              <Tooltip title="Only licensed plant-touching CRBs with sales imports have a compliance score.">
                <HelpIcon />
              </Tooltip>
            </span>
          );
        } else if (row.compliance_score) {
          if (row.compliance_score > 89)
            return <span style={{ color: palette.primary.main }}>{row.compliance_score}%</span>;
          if (row.compliance_score > 79)
            return <span style={{ color: palette.warning.main }}>{row.compliance_score}%</span>;
          return <span style={{ color: palette.error.main }}>{row.compliance_score}%</span>;
        } else {
          return '--';
        }
      }
    }
  ];

  const getDispensaryRows = (): Row<ActiveCrbRow>[] => {
    const activeDispensaries = model.activeDispensaries.map((dispensary: MinifiedDispensary) => {
      const {
        assigned_onboarding_template,
        name,
        city,
        state,
        compliance_score,
        orgType,
        muo_id,
        id,
        internal_metadata,
        industryType
      } = dispensary;
      const onboardingTemplates = getFiTemplateStore().templates.onboarding;
      const licenseType: string = getLicenseType(dispensary);
      const templateExists = Object.keys(onboardingTemplates).includes(
        assigned_onboarding_template.template_id
      );

      let nonPlantTouching = false;

      if (templateExists) {
        if (!onboardingTemplates[assigned_onboarding_template.template_id].plant_touching) {
          nonPlantTouching = true;
        }
      }

      let internalIds: string[] = [];
      if (internal_metadata && internal_metadata.accounts) {
        internalIds = [...internalIds, ...Object.keys(internal_metadata.accounts)];
      }

      return {
        id,
        data: {
          id,
          dispensary_name: name,
          city: city || '--',
          state: state,
          license_type: licenseType,
          reviewed_on: assigned_onboarding_template.reviewed_date,
          compliance_score: compliance_score,
          orgType: orgType,
          hideCannabisRelatedContent: NonCannabisIndustryTypes.includes(dispensary.industryType),
          industryType,
          parent_muo_name: dispStore.bankMuos.find((m) => m.id === muo_id)?.name,
          nonPlantTouching: nonPlantTouching,
          internalId: internalIds,
          relationship_manager: relationshipManagerStore.relationshipManagers[id] || '--',
          due_diligence: templateExists
            ? onboardingTemplates[assigned_onboarding_template.template_id].name
            : '--'
        }
      } as Row<ActiveCrbRow>;
    });

    return activeDispensaries;
  };

  const getMuoRows = (): Row<ActiveCrbRow>[] => {
    const filteredMuos = model.filteredBankMuos
      .filter((muo) => muo.assigned_onboarding_template.status !== DueDiligenceStatus.BANK_DISCONNECTED)
      .map((muo: MUOBankResponse) => {
        const { id, internal_metadata, name, city, state, assigned_onboarding_template, orgType } = muo;
        let internalIds: string[] = [];

        if (internal_metadata && internal_metadata.accounts) {
          internalIds = [...internalIds, ...Object.keys(internal_metadata.accounts)];
        }
        return {
          id,
          data: {
            id,
            dispensary_name: name,
            city: city,
            state: state,
            license_type: '--',
            reviewed_on: assigned_onboarding_template.reviewed_date,
            compliance_score: undefined,
            orgType: orgType,
            internalId: internalIds,
            relationship_manager: relationshipManagerStore.relationshipManagers[id] || '--',
            due_diligence: '--'
          },
          disableSelect: true,
          selectTooltip: 'You cannot change the status of a parent account'
        } as Row<ActiveCrbRow>;
      });

    return filteredMuos;
  };

  const getTableRows = () => {
    const rows = [...getDispensaryRows(), ...getMuoRows()];

    if (model.accountsSearchTerm.length === 0) {
      return rows;
    }

    const term = model.accountsSearchTerm.toLowerCase();

    return rows.filter((row) => {
      const dispensary = row.data.dispensary_name.toLowerCase();
      const hasDispensaryOfInternalIds = row.data.internalId?.some(
        (internalId) => internalId.toLowerCase().indexOf(term.toLowerCase()) != -1
      );
      return dispensary.indexOf(term) != -1 || hasDispensaryOfInternalIds;
    });
  };

  const tableRows: Row<ActiveCrbRow>[] = getTableRows();

  const showFilterZeroState = React.useMemo(() => {
    return tableRows.length === 0 && (model.accountsSearchTerm.length > 0 || model.searchTerms.length > 0);
  }, [tableRows, model.accountsSearchTerm.length, model.searchTerms]);

  const handleOnView = (id: string) => {
    navigate(`/secure/fi/accounts/${id}/view`);
  };

  if (model.isSorting) {
    return <Spinner />;
  }

  return (
    <>
      <Box>
        {showFilterZeroState || model.activeDispensaries.length || model.filteredBankMuos.length ? (
          <TableContainer>
            <Table
              dataCy="active-dispensaries-table"
              columns={columns}
              rows={tableRows}
              clickable
              onClick={handleOnView}
              showFilterZeroState={showFilterZeroState}
              defaultSort={
                appViewStore.accountPaginationFilters.active.defaultSort
                  ? (appViewStore.accountPaginationFilters.active.defaultSort as keyof ActiveCrbRow)
                  : 'dispensary_name'
              }
              defaultSortDirection={
                appViewStore.accountPaginationFilters.active.defaultSortDirection
                  ? appViewStore.accountPaginationFilters.active.defaultSortDirection
                  : 'asc'
              }
              onSort={(sort, sortDirection) => {
                appViewStore.accountPaginationFilters.active.defaultSort = sort;
                appViewStore.accountPaginationFilters.active.defaultSortDirection = sortDirection;
              }}
              defaultRowCount={
                appViewStore.accountPaginationFilters.active.pageSize
                  ? appViewStore.accountPaginationFilters.active.pageSize
                  : 10
              }
              onRowsPerPageChange={(pageSize: number) => {
                appViewStore.accountPaginationFilters.active.pageSize = pageSize;
              }}
              page={appViewStore.accountPaginationFilters.active.page}
              onPageChange={(page) => {
                appViewStore.accountPaginationFilters.active.page = page;
              }}
              selectable={model.canUserChangeStatus}
              returnSelection={(selected) => {
                setSelectedAccounts(selected);
              }}
              actions={
                <Container padding="0" align="center">
                  <ContainerItem>
                    <Text type="table-header" content={`${selectedAccounts.length} Selected`} />
                  </ContainerItem>

                  <Button
                    label={'Change Status'}
                    onClick={() => setChangeStatusModalOpen(true)}
                    color={'text'}
                    startIcon={<SwapHorizIcon />}
                  />
                </Container>
              }
            />
          </TableContainer>
        ) : (
          <ZeroState
            title={'You currently have no active accounts'}
            subTitle={
              'Start by approving accounts in Green Check. All active accounts and their details will be available here once approved.'
            }
            icon={'/img/empty-states/active-account.svg'}
          />
        )}
      </Box>
      <ChangeStatusModal
        isOpen={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        initialStatus={DueDiligenceStatus.BANK_APPROVED}
      />
    </>
  );
});
